import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ActionBar from './ActionBar';

/**
 * Whether the seller arrived straight from publishing this listing. The edit
 * page's redirect sets `listingJustSubmitted` in the router state.
 *
 * Read once, then removed from the browser's history entry, so the receipt is
 * a one-off: a refresh or a later visit gets the ordinary pending bar. It is
 * cleared with `replaceState` directly rather than through the router, which
 * would re-run the page's data loading for nothing.
 */
const useListingJustSubmitted = () => {
  const location = useLocation();
  const [justSubmitted] = useState(() => !!location?.state?.listingJustSubmitted);

  useEffect(() => {
    if (!justSubmitted || typeof window === 'undefined') {
      return;
    }
    const entry = window.history.state;
    if (entry?.state?.listingJustSubmitted) {
      const { listingJustSubmitted, ...restState } = entry.state;
      window.history.replaceState({ ...entry, state: restState }, '');
    }
  }, [justSubmitted]);

  return justSubmitted;
};

/**
 * Shared author-facing notification/edit bars for listing page variants.
 *
 * @param {Object} props
 * @param {boolean} props.mounted - True when component has mounted on client
 * @param {Object} props.listing - Current listing entity
 * @param {boolean} props.isOwnListing - True if current user is listing author
 * @param {boolean} props.noPayoutDetailsSetWithOwnListing - True when payout warning should be shown
 * @param {Object} props.currentUser - Logged-in user
 * @param {Object} props.editParams - Listing edit route params
 * @param {string} props.className - Base class for ActionBar
 * @param {function} [props.onDeleteListing] - Opens "Slett annonse" for the seller's own listing
 * @returns {JSX.Element|null}
 */
const Notifications = props => {
  const {
    mounted,
    listing,
    isOwnListing,
    noPayoutDetailsSetWithOwnListing,
    currentUser,
    editParams,
    className,
    onDeleteListing,
  } = props;
  const isJustSubmitted = useListingJustSubmitted();

  // Keep rendering available to non-authors too, so closed-listing messaging is visible for all users.
  const canShowBars = mounted && listing?.id;
  if (!canShowBars) {
    return null;
  }

  return (
    <>
      {noPayoutDetailsSetWithOwnListing ? (
        <ActionBar
          className={className}
          isOwnListing={isOwnListing}
          listing={listing}
          showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
          currentUser={currentUser}
        />
      ) : null}
      <ActionBar
        className={className}
        isOwnListing={isOwnListing}
        listing={listing}
        currentUser={currentUser}
        editParams={editParams}
        isJustSubmitted={isJustSubmitted}
        onDeleteListing={onDeleteListing}
      />
    </>
  );
};

export default Notifications;
